import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Components/Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { useAuth } from '../AuthContext';
import { POKEMON_TYPES, TYPE_COLORS, GENERATIONS, GENERATION_RANGES } from '../utils/pokemonTypes';

const POKEAPI = 'https://pokeapi.co/api/v2';

function spriteUrl(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemonId}.png`;
}

function smallSprite(pokemonId) {
  return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemonId}.png`;
}

function idFromUrl(url) {
  return parseInt(url.replace(/\/$/, '').split('/').pop(), 10);
}

// Every type's full Pokémon list only needs fetching once per type, ever.
const cachedTypePokemon = {};

// Mega Evolutions, Gigantamax forms, regional variants etc. get their own Pokémon id (10000+),
// which falls way outside the national dex ranges used for the generation filter below. Their
// species id (= the base Pokémon's national dex number) is what actually tells us the generation,
// so it's resolved lazily (one extra fetch per form, cached forever) and used instead.
const cachedSpeciesId = {};

// One favorite Pokémon per type, per generation (plus an overall "All" set). Mirrors the GOTY
// page's year-tabs: pick a generation tab, each of the 18 types has its own favorite slot there.
// Picking a favorite browses the actual Pokémon of that type (and generation, if one is
// selected), fetched straight from PokeAPI's /type endpoint - not a free-text search.
function PokemonTypes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedGen, setSelectedGen] = useState(0);

  const [editingType, setEditingType] = useState(null);
  const [editFilter, setEditFilter] = useState('');
  const [editSelected, setEditSelected] = useState(null);
  const [typePokemonList, setTypePokemonList] = useState([]);
  const [typePokemonLoading, setTypePokemonLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [speciesIdMap, setSpeciesIdMap] = useState({});

  const fetchFavorites = async () => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_type_favorites?select=*`, {
      headers: supabaseHeaders(false),
    });
    const data = await res.json();
    setFavorites(Array.isArray(data) ? data : []);
  };

  useEffect(() => { fetchFavorites(); }, []);

  const favoriteFor = (type, generation) =>
    favorites.find((f) => f.type === type && f.generation === generation);

  // Forms (id >= 10000) don't carry a national dex id, so fetch their species to find it.
  const resolveFormSpecies = async (list) => {
    const toResolve = list
      .map((p) => idFromUrl(p.url))
      .filter((id) => id >= 10000 && !(id in cachedSpeciesId));
    if (toResolve.length === 0) return;
    await Promise.all(toResolve.map(async (id) => {
      try {
        const res = await fetch(`${POKEAPI}/pokemon/${id}`);
        const data = await res.json();
        cachedSpeciesId[id] = idFromUrl(data.species.url);
      } catch {
        cachedSpeciesId[id] = id;
      }
    }));
    setSpeciesIdMap({ ...cachedSpeciesId });
  };

  const loadTypePokemon = async (type) => {
    if (cachedTypePokemon[type]) {
      setTypePokemonList(cachedTypePokemon[type]);
      resolveFormSpecies(cachedTypePokemon[type]);
      return;
    }
    setTypePokemonLoading(true);
    try {
      const res = await fetch(`${POKEAPI}/type/${type}`);
      const data = await res.json();
      const list = (data.pokemon || []).map((p) => p.pokemon);
      cachedTypePokemon[type] = list;
      setTypePokemonList(list);
      resolveFormSpecies(list);
    } catch (err) {
      console.error('Failed to fetch type pokemon:', err);
      setTypePokemonList([]);
    } finally {
      setTypePokemonLoading(false);
    }
  };

  const genIdFor = (p) => {
    const id = idFromUrl(p.url);
    return id >= 10000 ? (speciesIdMap[id] || id) : id;
  };

  const browsablePokemon = (() => {
    let list = typePokemonList;
    if (selectedGen) {
      const range = GENERATION_RANGES[selectedGen];
      if (range) {
        const [min, max] = range;
        list = list.filter((p) => {
          const id = genIdFor(p);
          return id >= min && id <= max;
        });
      }
    }
    if (editFilter.trim()) {
      const q = editFilter.toLowerCase().trim();
      list = list.filter((p) => p.name.includes(q));
    }
    return list;
  })();

  const openEdit = (type) => {
    const existing = favoriteFor(type, selectedGen);
    setEditingType(type);
    setEditFilter('');
    setEditSelected(existing ? { name: existing.name, pokemonId: existing.pokemon_id } : null);
    setEditStatus('');
    loadTypePokemon(type);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editSelected) { setEditStatus('Selecteer een Pokémon.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_type_favorites?on_conflict=generation,type`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'resolution=merge-duplicates' },
        body: JSON.stringify({
          type: editingType,
          generation: selectedGen,
          name: editSelected.name,
          pokemon_id: editSelected.pokemonId,
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditStatus('Saved!');
      setTimeout(() => { setEditingType(null); fetchFavorites(); }, 800);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleClear = async () => {
    const existing = favoriteFor(editingType, selectedGen);
    if (!existing) { setEditingType(null); return; }
    if (!window.confirm(`Clear favorite for ${editingType}?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_type_favorites?id=eq.${existing.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Clear failed');
      setEditingType(null);
      fetchFavorites();
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Favorite by Type</h1>
        </div>
        <div className="controls">
          <button onClick={() => navigate('/pokemon')} className="header-nav-btn">←</button>
          {user && (
            <button onClick={() => setEditMode((m) => !m)} className={editMode ? 'btn-active' : ''}>
              Edit
            </button>
          )}
        </div>
        <div className="tab-pills" style={{ marginBottom: '1rem' }}>
          {GENERATIONS.map((gen) => (
            <button
              key={gen}
              type="button"
              className={gen === selectedGen ? 'tab-pill tab-pill--active' : 'tab-pill'}
              onClick={() => setSelectedGen(gen)}
            >
              {gen === 0 ? 'All' : gen}
            </button>
          ))}
        </div>
      </div>

      <div className="todo-grid">
        {POKEMON_TYPES.map((t, idx) => {
          const fav = favoriteFor(t.value, selectedGen);
          return (
            <div
              className="todo-card"
              key={t.value}
              style={{ '--index': idx, cursor: editMode ? 'pointer' : 'default', borderColor: TYPE_COLORS[t.value] }}
              onClick={editMode ? () => openEdit(t.value) : undefined}
            >
              {fav ? (
                <img
                  src={spriteUrl(fav.pokemon_id)}
                  alt={fav.name}
                  className="todo-card-poster todo-card-poster--pokemon"
                  onError={(e) => { e.target.onerror = null; e.target.src = smallSprite(fav.pokemon_id); }}
                />
              ) : (
                <div className="todo-card-poster todo-card-poster--pokemon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>—</div>
              )}
              <div className="todo-card-title">{t.label}</div>
              {fav && <div className="todo-card-subtitle" style={{ textTransform: 'capitalize' }}>{fav.name}</div>}
            </div>
          );
        })}
      </div>

      {editingType && (
        <Modal onClose={() => setEditingType(null)}>
          <h2 className="modal-title" style={{ textTransform: 'capitalize' }}>
            {editingType} {selectedGen === 0 ? '(All)' : `(Gen ${selectedGen})`}
          </h2>
          <form className="modal-form" onSubmit={handleEditSubmit}>
            {editSelected ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <img src={spriteUrl(editSelected.pokemonId)} alt={editSelected.name} style={{ width: 140 }} />
                <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                  {editSelected.name} #{String(editSelected.pokemonId).padStart(3, '0')}
                </span>
                <button type="button" className="form-submit btn-cancel" onClick={() => setEditSelected(null)}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Filter</label>
                  <input
                    type="text"
                    value={editFilter}
                    onChange={(e) => setEditFilter(e.target.value)}
                    placeholder="Typ om te filteren..."
                    autoFocus
                    autoComplete="off"
                  />
                </div>

                {typePokemonLoading && <p className="form-status">Loading...</p>}

                {!typePokemonLoading && (
                  <div className="pokemon-browse-grid">
                    {browsablePokemon.map((p) => {
                      const pid = idFromUrl(p.url);
                      return (
                        <div
                          key={p.name}
                          className="pokemon-browse-item"
                          onClick={() => setEditSelected({ name: p.name, pokemonId: pid })}
                        >
                          <img src={smallSprite(pid)} alt={p.name} />
                          <span style={{ textTransform: 'capitalize' }}>{p.name}</span>
                        </div>
                      );
                    })}
                    {browsablePokemon.length === 0 && <p className="no-results">Geen Pokémon gevonden.</p>}
                  </div>
                )}
              </>
            )}

            {editStatus && <p className="form-status">{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={() => setEditingType(null)}>
                Cancel
              </button>
              <button type="button" className="form-submit btn-delete" onClick={handleClear}>
                Clear
              </button>
              <button type="submit" className="form-submit" disabled={editLoading || !editSelected}>
                {editLoading ? 'Working...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PokemonTypes;
